// Custom language switcher: globe + current flag + chevron, opening a dark
// dropdown with both languages (the active one highlighted). Replaces the old
// react-select version. Flags are bundled SVGs (render consistently everywhere,
// unlike emoji flags which break on Windows).
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IoGlobeOutline, IoChevronDown } from 'react-icons/io5';
import gbFlag from '../assets/flags/gb.svg';
import saFlag from '../assets/flags/sa.svg';

const LANGS = [
  { value: 'ar', label: 'العربية', flag: saFlag, dir: 'rtl' },
  { value: 'en', label: 'English', flag: gbFlag, dir: 'ltr' },
];

const flagStyle = {
  width: 26,
  height: 18,
  borderRadius: 4,
  objectFit: 'cover',
  display: 'block',
  flexShrink: 0,
};

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = LANGS.find((l) => l.value === i18n.language) || LANGS[1];
  const isRtl = i18n.language === 'ar';

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (value) => {
    i18n.changeLanguage(value);
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Language"
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: '#1a1a1a',
          padding: '4px 6px',
        }}
      >
        <IoGlobeOutline size={22} color="#1a1a1a" />
        <img src={current.flag} alt="" style={{ ...flagStyle, boxShadow: '0 0 0 1px rgba(0,0,0,.15)' }} />
        <IoChevronDown
          size={16}
          color="#1a1a1a"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
        />
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 10px)',
            [isRtl ? 'right' : 'left']: 0,
            minWidth: 190,
            background: '#221d3b',
            borderRadius: 14,
            padding: 8,
            boxShadow: '0 12px 34px rgba(0,0,0,.35)',
            zIndex: 2000,
          }}
        >
          {LANGS.map((l) => {
            const active = l.value === i18n.language;
            return (
              <button
                key={l.value}
                type="button"
                dir={l.dir}
                role="option"
                aria-selected={active}
                onClick={() => pick(l.value)}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,.08)'; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  width: '100%',
                  background: active ? '#6E3FD3' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#fff',
                  fontSize: 16,
                  fontWeight: 600,
                  padding: '11px 14px',
                  borderRadius: 10,
                  textAlign: 'start',
                  transition: 'background .15s',
                }}
              >
                <img src={l.flag} alt="" style={flagStyle} />
                <span>{l.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
